/**
 * One of a few views, as iOS's segmented control (see /styleguide): the one
 * shown raised on a grey track. A radio group to the keyboard: one Tab stop,
 * the arrow keys moving to (and choosing) the previous or next option.
 */
import * as preact from "preact";

import type { SelectOption } from "ui/Select";

interface Props {
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  "aria-label"?: string;
}

const arrowKeySteps: { [key: string]: number | undefined } = {
  ArrowLeft: -1,
  ArrowUp: -1,
  ArrowRight: 1,
  ArrowDown: 1,
};

export default class SegmentedControl extends preact.Component<Props> {
  buttons: (HTMLButtonElement | null)[] = [];

  render() {
    const { options, value } = this.props;
    // The chosen option is the Tab stop (the first if none is chosen)
    const tabStopIndex = Math.max(
      0,
      options.findIndex((option) => option.value === value)
    );
    return (
      <div
        class="segmentedControl"
        role="radiogroup"
        aria-label={this.props["aria-label"]}
      >
        {options.map((option, index) => (
          <button
            type="button"
            key={option.value}
            ref={(button) => {
              this.buttons[index] = button;
            }}
            role="radio"
            aria-checked={option.value === value}
            tabIndex={index === tabStopIndex ? 0 : -1}
            class={option.value === value ? "selected" : undefined}
            onClick={() => this.props.onChange(option.value)}
            onKeyDown={(event) => this.keyDown(event, index)}
          >
            {option.label}
          </button>
        ))}
      </div>
    );
  }

  keyDown(event: KeyboardEvent, index: number) {
    const step = arrowKeySteps[event.key];
    if (!step) return;
    event.preventDefault();
    const options = this.props.options;
    const nextIndex = (index + step + options.length) % options.length;
    this.buttons[nextIndex]?.focus();
    this.props.onChange(options[nextIndex].value);
  }
}
